import classNames from 'classnames';
import { parseISO, format, parse } from 'date-fns';
import uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useSearch } from 'wouter';

import useEvents from '../../hooks/useEvents';
import useSortedVenues from '../../hooks/useSortedVenues';

import { useProjectionsSite } from '../../contexts/SiteContext';
import WeekEventCard from '../cards/WeekEvent';
import Select from '../fields/Select';
import Projectron from '../icons/Projectron';
import Title from '../typography/Title';
import Detector from './Detector';

import styles from '../../../styles/partials/events.module.css';

const propTypes = {
    query: PropTypes.shape({}),
    count: PropTypes.number,
    className: PropTypes.string,
};

function EventsByDate({ query: initialQuery = null, count = 12, className = null }) {
    const searchString = useSearch();
    const finalVenues = useSortedVenues();
    const projectionsSite = useProjectionsSite();
    const { url: projectionsUrl = null } = projectionsSite || {};

    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);

    const baseQuery = useMemo(
        () =>
            initialQuery !== null
                ? { ...initialQuery, order: 'start_date', upcoming: true }
                : { order: 'start_date', upcoming: true },
        [initialQuery],
    );

    const [query, setQuery] = useState(
        // eslint-disable-next-line no-nested-ternary
        searchString !== null ? { ...queryString.parse(searchString), ...baseQuery } : baseQuery,
    );
    const { venue = null } = query || {};
    const onVenueChange = useCallback(
        (value) => {
            setQuery({ ...query, venue: value });
            setItems([]);
            setPage(1);
        },
        [query, setQuery],
    );

    const eventsQuery = useMemo(() => ({ count, query, page }), [query, page]);
    const { data: events = null, isLoading = false } = useEvents(eventsQuery);
    const empty = items.length === 0;
    const ended = events === null || events.length < count;

    useEffect(() => {
        if (events !== null) {
            setItems((prevItems) => uniqBy([...prevItems, ...events], 'id'));
        }
    }, [events]);

    const onPageEnd = useCallback(() => {
        if (!isLoading && !ended) {
            setPage(page + 1);
        }
    }, [page, setPage, isLoading, ended]);

    const itemsByDate = useMemo(() => {
        if (items === null) {
            return null;
        }
        return items.reduce((acc, event) => {
            const { startDate = null } = event || {};
            if (startDate === null) {
                return acc;
            }
            const localizedDate = format(parseISO(startDate), 'yyyy-MM-dd');
            if (!acc[localizedDate]) {
                acc[localizedDate] = [];
            }
            acc[localizedDate].push(event);
            return acc;
        }, {});
    }, [items]);

    return (
        <div
            className={classNames([
                styles.container,
                styles.week,
                { [className]: className !== null },
            ])}
        >
            <div className={styles.header}>
                <Title className={styles.title} size="small">
                    <FormattedMessage defaultMessage="Filters" description="Section title" />
                </Title>
                <div className={styles.filters}>
                    <span>↓</span>
                    <Select
                        className={styles.select}
                        value={venue}
                        options={finalVenues || []}
                        onChange={onVenueChange}
                        wide
                    />
                </div>
            </div>
            <div className={styles.list}>
                {Object.keys(itemsByDate || {}).map((date) => {
                    const cards = itemsByDate[date];
                    const parsedDate = parse(`${date} 00:00:00`, 'yyyy-MM-dd HH:mm:ss', new Date());
                    return (
                        <div key={`date-${parsedDate}`} className={styles.group} title={date}>
                            <Title className={styles.title} size="large">
                                <FormattedDate value={parsedDate} day="numeric" month="long" />
                            </Title>
                            <div className={styles.grid}>
                                {(cards || []).map((event) => (
                                    <WeekEventCard
                                        key={`event-${event?.id}`}
                                        className={styles.card}
                                        withDocWeek={false}
                                        item={event}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
                {isLoading ? (
                    <div className={styles.loading}>
                        <div className={styles.top}>
                            <Title className={styles.title} size="small">
                                <Projectron className={styles.loadingIcon} image={null} />
                                <FormattedMessage
                                    defaultMessage="Loading"
                                    description="Status label"
                                />
                            </Title>
                        </div>
                        <div className={styles.grid}>
                            <WeekEventCard className={styles.card} isPlaceholder />
                            <WeekEventCard className={styles.card} isPlaceholder />
                            <WeekEventCard className={styles.card} isPlaceholder />
                        </div>
                    </div>
                ) : null}
                {!isLoading && !empty && !ended ? (
                    <Detector onEnter={onPageEnd}>Loading</Detector>
                ) : null}
                {!isLoading && empty && ended ? (
                    <div className={classNames([styles.empty, styles.large])}>
                        <p className={styles.paragraph}>
                            <FormattedMessage
                                defaultMessage="Semaine du Doc is over for now. See you next year!"
                                description="No events found"
                            />
                        </p>
                        {projectionsUrl !== null ? (
                            <p className={styles.paragraph}>
                                <a href={projectionsUrl}>
                                    <FormattedMessage
                                        defaultMessage="Go to Projections Tënk"
                                        description="No events found"
                                    />
                                </a>
                            </p>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

EventsByDate.propTypes = propTypes;

export default EventsByDate;
